import React, { useState } from 'react';
import { Modal, Radio, Input, Button, Space, message } from 'antd';
import { archiveApplication } from '../../services/api';
import { ArchiveReason } from '../../types';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import css from './ArchiveModal.module.scss';

const { TextArea } = Input;

interface ArchiveModalProps {
  applicationId: string | null;
  applicationTitle: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const archiveReasons: { value: ArchiveReason; label: string }[] = [
  { value: 'employer_rejected', label: 'Rejected by employer' },
  { value: 'accepted_offer', label: 'Accepted an offer' },
  { value: 'position_filled', label: 'Position was filled' },
  { value: 'no_response', label: 'No response received' },
  { value: 'other', label: 'Other' },
];

const ArchiveModal: React.FC<ArchiveModalProps> = ({
  applicationId,
  applicationTitle,
  open,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState<ArchiveReason | null>(null);
  const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setReason(null);
    setOtherText('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason || !applicationId) {
      message.error('Please select a reason');
      return;
    }

    if (reason === 'other' && !otherText.trim()) {
      message.error('Please provide a reason');
      return;
    }

    setLoading(true);

    try {
      await archiveApplication(applicationId, {
        archiveReason: reason,
        archiveReasonText: reason === 'other' ? otherText.trim() : undefined,
      });
      message.success('Application archived');
      captureEvent('application_archived', { archive_reason: reason });
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Archive application error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const isValid = reason && (reason !== 'other' || otherText.trim());

  return (
    <Modal
      title="Archive Application"
      width="min(520px, 94vw)"
      open={open}
      onCancel={handleClose}
      footer={null}
      className={css.archiveModal}
    >
      <p className={css.subtitle}>
        Why are you archiving "{applicationTitle}"?
      </p>

      <Radio.Group
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className={css.radioGroup}
      >
        <Space direction="vertical" size="middle">
          {archiveReasons.map((r) => (
            <Radio key={r.value} value={r.value}>
              {r.label}
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      {reason === 'other' && (
        <TextArea
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Please describe your reason..."
          rows={3}
          maxLength={500}
          className={css.textArea}
        />
      )}

      <div className={css.actions}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!isValid}
        >
          Archive Application
        </Button>
      </div>
    </Modal>
  );
};

export default ArchiveModal;
